/**
 * User Info Composable
 * 使用者資訊管理
 * 原始來源: machines/user_info.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/user/userInfo.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { useUserInfoStore } from '@/stores/user/userInfo'
import { userApi, activityApi } from '@/api'
import { computed } from 'vue'

export const useUserInfo = () => {
  const { $request } = useNuxtApp()
  const { appSecret } = useRuntimeConfig().public

  const store = useUserInfoStore()
  const { status, userInfo, error, loading, isFetching, isUpdating, getUserInfo, hasUserInfo, isSettleEnable, isVerified, profileGroup } = storeToRefs(store)

  const uidSite = computed(() => userInfo.value?.uid_site)
  const userAvatar = computed(() => {
    const { photo } = userInfo.value || {}
    const { user } = window?.Telegram?.WebApp?.initDataUnsafe || {}
    return user?.photo_url || photo
  })
  const userNickname = computed(() => {
    const { nickname } = userInfo.value || {}
    const { user } = window?.Telegram?.WebApp?.initDataUnsafe || {}
    return user?.username || nickname
  })

  // API Methods
  const fetchUserInfo = async () => {
    store.setStatus('fetching')
    store.setError(null)

    try {
      const response = await $request.send(userApi.getUserInfo())
      const data = response.data || response

      store.setUserInfo(data || {})
      store.setStatus('idle')
      return data
    } catch (error) {
      store.setError(error)
      store.setStatus('idle')
      throw error
    }
  }

  const updateUserInfo = async (data) => {
    store.setStatus('updating')
    store.setError(null)

    try {
      const response = await $request.send(userApi.updateUserInfo(data))
      const result = response.data || response

      // Note: 因為更新 userInfo 後 get self 還是拿到舊資料
      // 所以這裡不重新 fetch，而是直接更新 state
      // 如果需要最新資料，可以手動呼叫 fetchUserInfo

      store.setStatus('idle')
      return result
    } catch (error) {
      store.setError(error)
      store.setStatus('idle')
      throw error
    }
  }

  const setUserInfo = (data) => {
    store.setUserInfo(data)
  }

  const fetchLevelBonusInfo = async () => {
    const { data } = await $request.send(activityApi.getLevelBonusInfo({ appSecret }))
    store.setLevelBonusInfo(data)
  }

  const fetchProfile = async () => {
    store.setStatus('fetchingProfile')
    store.setError(null)

    try {
      const response = await $request.send(userApi.getUserProfile())
      const data = response.data || response

      store.setProfileGroup(data || {})
      store.setStatus('idle')
      return data
    } catch (error) {
      store.setError(error)
      store.setStatus('idle')
      throw error
    }
  }
  return {
    status,
    uidSite,
    userInfo,
    profileGroup,
    error,
    loading,
    isFetching,
    isUpdating,
    getUserInfo,
    hasUserInfo,
    userAvatar,
    userNickname,
    isVerified,
    isSettleEnable,
    fetchUserInfo,
    updateUserInfo,
    setUserInfo,
    fetchLevelBonusInfo,
    fetchProfile,
  }
}
